import React, { useContext, useEffect, useRef } from 'react';
import { node } from 'prop-types';
import {
  Card,
  CardTitle,
  CardContent,
  Col,
  Row
} from '@thd-olt-component-react/core-ui';
import { extend, QueryContext, useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { SpecialBuyBanner } from '@thd-olt-component-react/special-buy-banner';
import { StoreDetails, StoreRecycleProgram, StoreDetailsSEO } from '@thd-olt-component-react/store-details';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { TrendingCategories } from '@thd-olt-component-react/thd-recs-containers';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { SponsoredCarousel } from '@thd-olt-component-react/sponsored-content';
import {
  StoreHero,
  StoreHeroHeader,
  StoreHeroDetails,
  StoreHeroCurbside,
  StoreHeroWorkshop,
  StoreHeroFeatures
} from '@thd-olt-component-react/store-hero';
import {
  StoreRatingsAndReviewsDesktopImporter,
  StoreRatingsAndReviewsMobileImporter
} from '@thd-olt-component-react/ratings-and-reviews';
import { StorePod, StorePodServices } from '@thd-olt-component-react/store-pod';
import { Metadata, StoreDetailMetadata } from '@thd-olt-component-react/metadata';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';
import { NearbyStores } from '../../shared-components/NearbyStores';
import './details.scss';

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};

ZoneCard.propTypes = {
  children: node.isRequired
};

export const Details = () => {
  declareContexts([QueryContext, ExperienceContext]);
  const { channel } = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const isMobile = channel === 'mobile';
  const path = useLocation();
  const ratingsReviewsRef = useRef();
  const {
    storeNumber, storeZip, name, state, city, reviewPage
  } = useParams();
  const idPopularCategories = '1zLZzakuSVhv8ssZ8p8IIJ';

  const options = {
    variables: {
      storeSearchInput: storeNumber,
    }
  };

  const { data } = useDataModel('storeSearch', options);
  const storeDetailsPageLink = data?.storeSearch?.stores?.[0]?.storeDetailsPageLink;
  const seoPageNumber = reviewPage ? parseInt(reviewPage, 10) : null;

  const onRatingClick = () => {
    ratingsReviewsRef.current.scrollIntoView()
  };

  useEffect(() => {
    if (storeDetailsPageLink && !path.pathname.includes(storeDetailsPageLink)) {
      window.location = storeDetailsPageLink + window?.location?.search;
    }
  }, [storeDetailsPageLink]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.details-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city
    });
  }, [storeNumber]);

  const mapOptions = {
    disableOnMapViewChange: true,
    disableOnStoreClick: true,
    height: '1040px',
    omitStoreNumber: storeNumber
  };

  const resultsOptions = {
    maxNumberOfResults: 3,
    omitStoreNumber: storeNumber
  }

  const headerFont = {
    size: isMobile ? 'medium' : 'xlarge'
  };

  const storeHeroWrapperClasses = isMobile ? '' : 'details__store-hero-wrapper';

  return (
    <>
      <ErrorBoundary name="sf-details-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>
      <ErrorBoundary name="sf-details-metadata">
        <Metadata>
          <StoreDetailMetadata storeId={storeNumber} seoPageNumber={seoPageNumber} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="sf-details-content">
        <Row className="isBound">
          <Row>
            <Col nopadding fallback={12}>
              <StoreFinderBreadcrumbs includeDirectory />
            </Col>
          </Row>
          <Row>
            <Col nopadding className={storeHeroWrapperClasses}>
              <StoreHero className="details__store-hero" storeId={storeNumber}>
                <StoreHeroHeader font={headerFont} onRatingClick={onRatingClick} />
                <Row className="details__store-hero-wrapper">
                  <Col className="details__store-detail-wrapper" xs={12} sm={4}>
                    <StoreHeroDetails className="details__store-hero-block" />
                  </Col>
                  <Col className="details__store-detail-wrapper" xs={12} sm={4}>
                    <StoreHeroCurbside className="details__store-hero-block" />
                  </Col>
                  {isMobile && (
                    <>
                      <Col xs={12} sm={2}>
                        <StoreHeroWorkshop />
                      </Col>
                      <Col xs={12} sm={2}>
                        <StoreHeroFeatures className="details__store-hero-block" showServices />
                      </Col>
                    </>
                  )}
                  {!isMobile && (
                    <Col className="details__store-detail-wrapper" xs={12} sm={4}>
                      <StoreHeroFeatures className="details__store-hero-block" />
                    </Col>
                  )}
                </Row>
              </StoreHero>
            </Col>
          </Row>
          <Row>
            <Col className="details__special-buy-banner" nopadding fallback={12}>
              <SpecialBuyBanner specialBuyType="sbotd" />
            </Col>
          </Row>
          <SponsoredCarousel
            pageContext={{
              schema: 'storepage_sponsored',
              isStorePage: true
            }}
            mockEnabledFeatureSwitch
            dynamic={{
              pageType: 'store-details'
            }}
            hydrator={{
              className: 'grid',
              scrollBuffer: 750
            }}
          />
          <Row>
            <Col fallback={12}>
              <TrendingCategories storeId={storeNumber} zipCode={storeZip} />
            </Col>
          </Row>
          <Row>
            <Col>
              <StoreDetails storeId={storeNumber}>
                <Row>
                  {!isMobile && (
                    <Col md={4} xs={12}>
                      <Card sharp>
                        <CardTitle title="Store Features" />
                        <CardContent>
                          <StorePod className="details__store-features" storeId={storeNumber}>
                            <StorePodServices disablePlaceholder showIcon />
                          </StorePod>
                        </CardContent>
                      </Card>
                    </Col>
                  )}
                  <Col md={8} xs={12}>
                    <StoreRecycleProgram className="details__store-recycle-program" />
                  </Col>
                </Row>
                <Row>
                  <Col fallback={12}>
                    <StoreDetailsSEO />
                  </Col>
                </Row>
              </StoreDetails>
            </Col>
          </Row>
          <Row>
            {!isMobile && (
              <NearbyStores
                isMobile={isMobile}
                mapOptions={mapOptions}
                resultsOptions={resultsOptions}
                storeNumber={storeNumber}
                title="Nearby Stores"
              />
            )}
          </Row>
          <Row>
            <Col>
              <div ref={ratingsReviewsRef}>
                {!isMobile && (
                  <StoreRatingsAndReviewsDesktopImporter
                    storeId={storeNumber}
                    category="store"
                    qctx={qctx}
                    seoPageNumber={seoPageNumber}
                    seoStoreLink={path.pathname}
                    layoutGroup={{
                      tag: Row,
                    }}
                    hydrator={{
                      className: 'grid',
                      tag: ZoneCard
                    }}
                  />
                )}
                {isMobile && (
                  <StoreRatingsAndReviewsMobileImporter
                    storeId={storeNumber}
                    category="store"
                    qctx={qctx}
                    seoPageNumber={seoPageNumber}
                    seoStoreLink={path.pathname}
                    layoutGroup={{
                      tag: Row,
                    }}
                    hydrator={{
                      className: 'grid',
                      tag: ZoneCard
                    }}
                  />
                )}
              </div>
            </Col>
          </Row>
          {isMobile && (
            <NearbyStores
              isMobile={isMobile}
              mapOptions={mapOptions}
              resultsOptions={resultsOptions}
              storeNumber={storeNumber}
              title="Nearby Stores"
            />
          )}
          <Row>
            <Col nopadding fallback={12}>
              <PopularCategories popCatId={storeNumber} />
            </Col>
          </Row>
          <Row>
            <Col nopadding fallback={12}>
              <EmtLinks path={path.pathname} />
            </Col>
          </Row>
        </Row>
      </ErrorBoundary>
    </>
  );
};

Details.displayName = 'Details';
Details.propTypes = {};

Details.dataModel = extend({},
  SpecialBuyBanner,
  Breadcrumbs,
  EmtLinks,
  StoreDetails,
  StoreFinderBreadcrumbs,
  StorePod,
  StoreHero,
  TrendingCategories,
  StoreRatingsAndReviewsDesktopImporter,
  StoreRatingsAndReviewsMobileImporter,
  PopularCategories,
  StoreFinderRedirector,
  NearbyStores,
  StoreDetailMetadata
);
